// Small drawing helpers shared by the homepage's canvas figures.

export type Point = [number, number];

export const clamp = (x: number, a: number, b: number) => Math.min(b, Math.max(a, x));
export const lerp = (a: number, b: number, q: number) => a + (b - a) * q;
export const easeInOut = (x: number) => {
  const q = clamp(x, 0, 1);
  return q < 0.5 ? 4 * q * q * q : 1 - Math.pow(-2 * q + 2, 3) / 2;
};

// A repeatable scatter in [0, 1), so a figure moves the same way on every visit.
export const hash = (k: number) => {
  const x = Math.sin(k * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
};

export function dot(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, colour: string) {
  ctx.fillStyle = colour;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
}

export function ring(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, colour: string, width = 1) {
  ctx.strokeStyle = colour;
  ctx.lineWidth = width;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.stroke();
}

export function stroke(ctx: CanvasRenderingContext2D, points: Point[], colour: string, width: number) {
  ctx.strokeStyle = colour;
  ctx.lineWidth = width;
  ctx.beginPath();
  points.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
  ctx.stroke();
}

export function bezier(p0: Point, p1: Point, p2: Point, p3: Point, steps: number): Point[] {
  return Array.from({ length: steps + 1 }, (_, i) => {
    const q = i / steps;
    const m = 1 - q;
    return [
      m * m * m * p0[0] + 3 * m * m * q * p1[0] + 3 * m * q * q * p2[0] + q * q * q * p3[0],
      m * m * m * p0[1] + 3 * m * m * q * p1[1] + 3 * m * q * q * p2[1] + q * q * q * p3[1],
    ];
  });
}

// A path with its running length, so a bead can move along it at an even speed.
export interface Polyline {
  points: Point[];
  lengths: number[];
}

export function polyline(points: Point[]): Polyline {
  const lengths = [0];
  for (let i = 1; i < points.length; i++) {
    lengths.push(lengths[i - 1] + Math.hypot(points[i][0] - points[i - 1][0], points[i][1] - points[i - 1][1]));
  }
  return { points, lengths };
}

// The point a fraction `u` of the way along the path.
export function pointAt({ points, lengths }: Polyline, u: number): Point {
  const target = lengths[lengths.length - 1] * clamp(u, 0, 1);
  let i = 1;
  while (i < lengths.length - 1 && lengths[i] < target) i++;
  const q = (target - lengths[i - 1]) / (lengths[i] - lengths[i - 1] || 1);
  return [lerp(points[i - 1][0], points[i][0], q), lerp(points[i - 1][1], points[i][1], q)];
}

// Geometry that depends only on the canvas size is built once for each size.
export function perSize<T>(build: (w: number, h: number) => T) {
  let cached: { key: string; value: T } | undefined;
  return (w: number, h: number): T => {
    const key = `${w}x${h}`;
    if (cached?.key !== key) cached = { key, value: build(w, h) };
    return cached.value;
  };
}
